import AccountService from '@/api/services/AccountService'
import ConnectionService from '@/api/services/ConnectionService'
import Account from '@/api/models/Account'
import { ActionContext, ActionTree } from 'vuex'
import { State } from '@/store/index'
import { RootStateMutations } from '@/store/mutations'
import ReferenceService from '@/api/services/ReferenceService'
import Store from '@/api/models/store/Store'
import PickupPointsRequest from '@/api/models/request/PickupPointsRequest'
import CdekCity from '@/api/models/CdekCity'
import StoreMap from '@/api/models/store/StoreMap'
import UpdateStoreMapRequest from '@/api/models/request/UpdateStoreMapRequest'
import CdekPickupPoint from '@/models/CdekPickupPoint'
import PickupPointRequest from '@/api/models/request/PickupPointRequest'
import { TariffGroup } from '@/api/models/TariffGroup'
import Package from '@/api/models/Package'
import AvailableTariffRequest from '@/api/models/request/AvailableTariffRequest'
import DeliveryRule from '@/api/models/DeliveryRule'
import SaveDeliveryRuleRequest from '@/api/models/request/SaveDeliveryRuleRequest'
import DeleteDeliveryRuleRequest from '@/api/models/request/DeleteDeliveryRuleRequest'
import { ProductProperty } from '@/models/ProductProperty'

const accountService = new AccountService()
const service = new ConnectionService()
const referenceService = new ReferenceService()

export const actions: ActionTree<State, State> = {
    async loadConnection (context: ActionContext<State, State>) {
        const connection = await service.getConnection()

        const conn = Object.assign({}, connection)

        context.commit(RootStateMutations.setConnection, conn)
        context.commit(RootStateMutations.setAccounts, conn.accounts)
    },
    async updateAccount (context: ActionContext<State, State>, account: Account) {
        const updateAccount = await accountService.updateAccount(account)
        const indexUpdatedAccount = context.state.accounts.findIndex((item) => item.id === updateAccount.id)

        if (indexUpdatedAccount !== -1) {
            context.state.accounts.splice(indexUpdatedAccount, 1, updateAccount)
        }

        context.commit(RootStateMutations.setCurrentAccountId, updateAccount.id)
    },
    async createAccount (context: ActionContext<State, State>, account: Account) {
        const createdAccount = await accountService.createAccount(account)

        if (createdAccount && createdAccount.id) {
            context.commit(RootStateMutations.setCurrentAccountId, createdAccount.id)
            context.commit(RootStateMutations.addAccount, createdAccount)
        }
    },
    async loadSystemStatuses (context: ActionContext<State, State>): Promise<void> {
        const statuses = await referenceService.getSystemStatusesList()
        context.commit(RootStateMutations.setSystemStatuses, statuses)
    },
    async loadCdekStatuses (context: ActionContext<State, State>): Promise<void> {
        const statuses = await referenceService.getCdekStatusesList()
        context.commit(RootStateMutations.setCdekStatuses, statuses)
    },
    async loadSystemProductStatuses (context: ActionContext<State, State>): Promise<void> {
        const statuses = await referenceService.getSystemProductStatusesList()
        context.commit(RootStateMutations.setSystemProductStatuses, statuses)
    },
    async loadSystemPaymentStatuses (context: ActionContext<State, State>, accountID: number): Promise<void> {
        const statuses = await referenceService.getSystemPaymentStatusesList(accountID)
        context.commit(RootStateMutations.setSystemPaymentStatuses, statuses)
    },
    async getStores(): Promise<Array<Store>> {
        return await referenceService.getSystemStores()
    },
    async getCdekPickupPoints(context: ActionContext<State, State>, request: PickupPointsRequest): Promise<Array<CdekPickupPoint>> {
        return await accountService.getCdekPickupPoints(request.accountId, request.storeCode, request.storeAddress)
    },
    async getCdekCities(context: ActionContext<State, State>, city: string): Promise<Array<CdekCity>> {
        return await referenceService.getCdekCities(city)
    },
    async getWarehousesMappings(context: ActionContext<State, State>, accountID: number): Promise<Array<StoreMap>> {
        return await accountService.getWarehousesMappings(accountID)
    },
    async getCdekCityByCode(context: ActionContext<State, State>, cityCode: number): Promise<CdekCity> {
        return await referenceService.getCdekCityByCityCode(cityCode)
    },
    async updateWarehouseMapping(context: ActionContext<State, State>, request: UpdateStoreMapRequest): Promise<void> {
        return await accountService.updateWarehouseMapping(request)
    },
    async getCdekPickupPointByCode(context: ActionContext<State, State>, request: PickupPointRequest): Promise<CdekPickupPoint> {
        return await accountService.getCdekPickupPointByCode(request.pointCode, request.accountId, request.storeAddress, request.cdekCityCode)
    },
    async getAllTariffs(): Promise<Array<TariffGroup>> {
        return await referenceService.getAllTariffs()
    },
    async getAvailableTariffs(context: ActionContext<State, State>, accountId: number): Promise<Array<number>> {
        return await accountService.getAvailableTariffs(accountId)
    },
    async updateAvailableTariffs(context: ActionContext<State, State>, request: AvailableTariffRequest): Promise<Array<number>> {
        return await accountService.updateAvailableTariffs(request.accountId, request.tariffs)
    },
    async getDeliveryRules(context: ActionContext<State, State>, accountId: number): Promise<Array<DeliveryRule>> {
        return await accountService.getDeliveryRules(accountId);
    },
    async saveDeliveryRules(context: ActionContext<State, State>, request: SaveDeliveryRuleRequest): Promise<DeliveryRule> {
        return await accountService.saveDeliveryRules(request);
    },
    async deleteDeliveryRule(context: ActionContext<State, State>, request: DeleteDeliveryRuleRequest): Promise<void> {
        return await accountService.deleteDeliveryRule(request.accountId, request.deliveryRuleId)
    },
    async getCdekPackages(): Promise<Array<Package>> {
        return await referenceService.getCdekPackages()
    },
    async getProductProperties(context: ActionContext<State, State>, accountId: number): Promise<Array<ProductProperty>> {
        return await referenceService.getProductProperties(accountId)
    }
}
